<template>
  <div class="b-mobile-cart">
    <div
      class="b-mobile-cart__navigation MOBILE_h13_Regular"
      @click="closeCart"
    >
      <IconArrowBack color="lighten2" width="18" height="18" />
      <span>{{ $t('mobileCart.ticketSelection') }}</span>
    </div>
    <div class="b-mobile-cart__header">
      <div class="b-mobile-cart__header-title MOBILE_h16_Semibold">
        {{ $t('mobileCart.yourOrder') }} ({{ numberOfTickets }})
      </div>
      <div class="b-mobile-cart__header-empty-cart" @click="emptyCart">
        {{ $t('mobileCart.emptyCart') }}
      </div>
    </div>
    <div class="b-mobile-cart__content">
      <BMobileCartItem
        v-for="(ticket, index) of tickets"
        :key="`${ticket.id}-${index}`"
        :ticket-data="{
          id: ticket.id,
          name: ticket.name,
          price: ticketPrice(ticket),
          currency: currency,
          discountName: ticket.discount?.name,
          seatId: ticket?.seat?.id,
          occurrenceName: ticket?.occurrenceName,
          occurrenceStartAt: ticket?.occurrenceStartAt,
          occurrenceEndAt: ticket?.occurrenceEndAt,
          timeslotName: ticket?.timeslotName,
          timeslotStartAt: ticket?.timeslotStartAt,
          timeslotEndAt: ticket?.timeslotEndAt
        }"
        :is-last="index === numberOfTickets - 1"
        @remove="removeTicket(ticket, index, ticket.name)"
      />
    </div>
    <div class="b-mobile-cart__discount">
      <div class="b-mobile-cart__discount-title MOBILE_h16_Semibold">
        {{ $t('mobileCart.discountTitle') }}
      </div>
      <div class="b-mobile-cart__discount-input">
        <InputText
          v-model.trim="discountCode"
          :placeholder="$t('mobileCart.discountCode')"
        />
      </div>
    </div>
    <div class="b-mobile-cart__divider" />
    <div class="b-mobile-cart__total">
      <div class="b-mobile-cart__total-title MOBILE_h16_Regular">
        {{ $t('mobileCart.total') }}
      </div>
      <div class="b-mobile-cart__total-price MOBILE_h18_Medium">
        {{ total }} {{ currency }}
      </div>
    </div>
    <div class="b-mobile-cart__checkout">
      <PButton
        :label="$t('mobileCart.checkout')"
        :disabled="!selectionValid"
        @click="submit"
      />
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import PButton from 'primevue/button';
import BMobileCartItem from '../general/BMobileCartItem.vue';
import IconArrowBack from '~/components/general/icons/IconArrowBack.vue';

export default {
  name: 'BMobileCart',
  components: {
    BMobileCartItem,
    InputText,
    PButton,
    IconArrowBack
  },
  props: {
    tickets: {
      type: Array,
      default: () => []
    },
    total: {
      type: String,
      default: '0.00'
    },
    currency: {
      type: String,
      default: ''
    },
    selectionValid: {
      type: Boolean,
      default: false
    },
    discountCodeValue: {
      type: String,
      default: null
    }
  },
  emits: ['close', 'empty', 'submit', 'remove'],
  data() {
    return {
      discountCode: this.discountCodeValue
    };
  },
  computed: {
    // Method to calculate the number of tickets in the cart
    numberOfTickets() {
      return this.tickets.length;
    }
  },
  methods: {
    // Method for returning the final price
    ticketPrice(ticket) {
      return ticket.donationAmount ?? ticket.finalPrice;
    },
    // Method to close the cart
    closeCart() {
      this.$emit('close');
    },

    // Method to empty the cart
    emptyCart() {
      this.$emit('empty');
      this.showEmptyCartMessage();
    },

    // Method to remove a ticket from the cart
    removeTicket(ticket, index, name) {
      this.$emit('remove', { ticket, index });
      this.showMessage(name);
    },

    // Method to show a message when a ticket is removed
    showMessage(name) {
      this.$notifier.showMessage({
        title: this.$t('mobileCart.removeTicket.title'),
        content: name + ' ' + this.$t('mobileCart.removeTicket.content'),
        type: 'success'
      });
    },

    // Method to show a message when cart is cleared
    showEmptyCartMessage() {
      this.$notifier.showMessage({
        title: this.$t('mobileCart.clearCart.title'),
        content: this.$t('mobileCart.clearCart.content'),
        type: 'success'
      });
    },

    // Method to submit the cart
    submit() {
      this.$emit('submit', this.discountCode);
    }
  }
};
</script>

<style lang="scss" scoped>
.b-mobile-cart {
  display: flex;
  flex-direction: column;
  background-color: var(--v-backgroundPrimary-lighten5);
  height: 100%;
  width: 100%;
  padding: 24px;

  &__navigation {
    color: var(--v-textPrimary-lighten2);
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 24px 0;

    &-title {
      color: var(--v-textPrimary-base);
    }

    &-empty-cart {
      color: var(--v-textPrimary-base);
      text-align: right;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 125%;
      text-decoration-line: underline;
    }
  }

  &__content {
    /* How much room will the content container take up in the flex container when there's
    extra space. (1 means it will take up all the extra space) */
    flex-grow: 1;
    overflow-y: auto;
  }

  &__discount {
    padding: 24px 0;

    &-title {
      color: var(--v-textPrimary-base);
    }

    &-input {
      padding-top: 16px;

      input,
      textarea {
        width: 100%;
      }
    }
  }

  &__divider {
    height: 0px;
    border-top: 1px var(--v-borderPrimary-lighten4) dashed;
    background-size: 5px 1px;
    padding-bottom: 16px;
  }

  &__total {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 24px 0;

    &-title,
    &-price {
      color: var(--v-textPrimary-base);
    }
  }

  &__checkout {
    button {
      width: 100%;
    }
    padding-top: 36px;
  }
}

@media screen and (orientation: landscape) {
  .b-mobile-cart {
    overflow-y: auto;

    &__content {
      /* How much an item can shrink when there's not enough space (0 means it won't shrink at all) */
      flex-shrink: 0;
    }
  }
}
</style>
